import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Checkbox } from "../ui/checkbox";
import { useToast } from "../../hooks/use-toast";
import { 
  ArrowLeft, 
  Mail, 
  Lock, 
  User, 
  Phone,
  Eye,
  EyeOff,
  CreditCard
} from "lucide-react";
import { authApi, referralApi } from "../../services/api";

const CustomerAuth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  const [signupData, setSignupData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false
  });

  const handleLoginChange = (field, value) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
  };

  const handleSignupChange = (field, value) => {
    setSignupData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast({
        title: "Missing information",
        description: "Please enter both email and password",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const result = await authApi.userLogin(loginData.email, loginData.password);
      if (result && result.token) {
        const user = result.user || {};
        localStorage.setItem('user_token', result.token);
        localStorage.setItem('customerAuth', JSON.stringify({
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
          email: user.email || loginData.email,
          id: user.id || '',
        }));
        toast({
          title: "Login successful",
          description: "Welcome back · redirecting to your dashboard…"
        });
        navigate("/dashboard");
      } else {
        toast({
          title: "Login failed",
          description: "Invalid email or password. If you don't have an account yet, sign up below.",
          variant: "destructive"
        });
      }
    } catch (e) {
      toast({ title: "Login error", description: "Network or server issue. Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    const { firstName, lastName, email, phone, password, confirmPassword, agreeTerms } = signupData;
    
    if (!firstName || !lastName || !email || !password) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical",
        variant: "destructive"
      });
      return;
    }

    if (!agreeTerms) {
      toast({
        title: "Terms required",
        description: "Please agree to the terms and conditions",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const reg = await authApi.register({
        firstName, lastName, email, phone, password, userType: 'customer'
      });
      if (reg && reg.success) {
        // Auto-login
        const login = await authApi.userLogin(email, password);
        if (login && login.token) {
          localStorage.setItem('user_token', login.token);
          localStorage.setItem('customerAuth', JSON.stringify({
            name: `${firstName} ${lastName}`,
            email,
            id: (login.user || {}).id || '',
          }));
          // WPS-027 · Attribute referral if a ref token is stored
          const refToken = localStorage.getItem('wunli_ref_token');
          const newUserId = (login.user || {}).id;
          if (refToken && newUserId) {
            try { await referralApi.convert(refToken, newUserId); } catch (_) {}
            localStorage.removeItem('wunli_ref_token');
          }
          toast({
            title: "Account created · Welcome to Wunli",
            description: "Let's design your first atelier card."
          });
          navigate("/dashboard");
        } else {
          toast({ title: "Account created", description: "Please sign in to continue." });
          setActiveTab("login");
        }
      } else {
        toast({
          title: "Signup failed",
          description: (reg && reg.message) || "This email may already be registered.",
          variant: "destructive"
        });
      }
    } catch (e) {
      toast({ title: "Signup error", description: "Network or server issue. Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {/* Animated gradient orbs · atelier noir + gold + tech blue */}
      <div style={{
        position: 'absolute', top: '-20%', left: '-10%',
        width: '60%', height: '60%',
        background: 'radial-gradient(circle, rgba(201,169,97,0.18) 0%, transparent 70%)',
        animation: 'wunli-pulse 8s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-30%', right: '-15%',
        width: '70%', height: '70%',
        background: 'radial-gradient(circle, rgba(2,132,199,0.15) 0%, transparent 70%)',
        animation: 'wunli-pulse 10s ease-in-out infinite 2s',
        pointerEvents: 'none',
      }} />

      {/* Grid pattern · suggests blueprint / printing */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
      }} />

      {/* Keyframes injected as inline style */}
      <style>{`
        @keyframes wunli-pulse {
          0%, 100% { transform: scale(1) translate(0,0); opacity: 0.7; }
          50% { transform: scale(1.1) translate(20px, -20px); opacity: 1; }
        }
        @keyframes wunli-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(2deg); }
        }
        @keyframes wunli-blink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0.3; }
        }
      `}</style>

      <div style={{
        position: 'relative',
        zIndex: 1,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 480px',
        minHeight: '100vh',
        gap: '0',
      }} className="customer-auth-grid">

        {/* LEFT · Hero panel */}
        <div style={{
          padding: '48px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: '#ffffff',
          position: 'relative',
        }} className="customer-auth-hero">

          {/* Top: back + brand */}
          <div>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                color: 'rgba(255,255,255,0.6)', fontSize: '13px',
                fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em',
                textTransform: 'uppercase', cursor: 'pointer',
              }}>
                <ArrowLeft className="h-4 w-4" /> Back to wunli.com
              </div>
            </Link>

            <div style={{ marginTop: '64px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '12px',
                marginBottom: '32px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '11px',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: '#c9a961',
              }}>
                <span style={{ width: '32px', height: '1px', background: '#c9a961' }} />
                Wunli · Print Studio
              </div>

              <h1 style={{
                fontSize: 'clamp(48px, 6vw, 80px)',
                fontWeight: 800,
                lineHeight: 0.95,
                letterSpacing: '-0.03em',
                color: '#ffffff',
                marginBottom: '24px',
              }}>
                Designed by <span style={{
                  background: 'linear-gradient(135deg, #c9a961 0%, #f4e4bc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>AI</span>.<br/>
                Crafted by <span style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>hand</span>.<br/>
                Printed to <span style={{ fontStyle: 'italic' }}>perfection</span>.
              </h1>

              <p style={{
                fontSize: '16px',
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.7)',
                maxWidth: '500px',
                marginBottom: '40px',
              }}>
                Atelier business cards for serious professionals. 22 industries · 44 curated looks · CMYK 300 DPI · reviewed by our team before printing.
              </p>
            </div>
          </div>

          {/* Middle · capability triad · AI + Design + Print */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            margin: '40px 0',
            maxWidth: '600px',
          }}>
            {[
              { icon: '🧠', label: 'AI Consult', sub: 'Wunli Agent suggests slogan · title · color · tier' },
              { icon: '🎨', label: 'Atelier Design', sub: 'Bold + Minimal · 22 industries · 44 looks' },
              { icon: '🖨️', label: 'Print-Ready', sub: 'CMYK · 300 DPI · 4over fulfillment' },
            ].map((c, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: '20px 16px',
                borderRadius: 0,
                animation: `wunli-float 6s ease-in-out infinite ${i * 0.8}s`,
              }}>
                <div style={{ fontSize: '28px', marginBottom: '12px' }}>{c.icon}</div>
                <div style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '11px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#c9a961',
                  fontWeight: 700,
                  marginBottom: '6px',
                }}>{c.label}</div>
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.4,
                }}>{c.sub}</div>
              </div>
            ))}
          </div>

          {/* Bottom · trust signals + LED indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            paddingTop: '24px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '10px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: '#22c55e',
                animation: 'wunli-blink 2s ease-in-out infinite',
              }} />
              Atelier · Live · Dallas TX
            </div>
            <div>Designed and Developed by Wunli.com</div>
          </div>
        </div>

        {/* RIGHT · Login form */}
        <div style={{
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          padding: '48px 40px',
        }} className="customer-auth-form-wrap">

        <Card className="shadow-xl border-0" style={{ width: '100%' }}>
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-3 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-full w-fit">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-2xl text-gray-900 mb-2">
              Customer Portal
            </CardTitle>
            <p className="text-gray-600 text-sm">
              Create an account or login to manage your business cards
            </p>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="loginEmail">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="loginEmail"
                        type="email"
                        placeholder="your.email@example.com"
                        value={loginData.email}
                        onChange={(e) => handleLoginChange("email", e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loginPassword">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="loginPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={(e) => handleLoginChange("password", e.target.value)}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <Link to="/forgot-password" className="text-emerald-600 hover:text-emerald-700">
                      Forgot password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white py-3"
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>

              {/* Signup Tab */}
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="firstName"
                          placeholder="John"
                          value={signupData.firstName}
                          onChange={(e) => handleSignupChange("firstName", e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        placeholder="Smith"
                        value={signupData.lastName}
                        onChange={(e) => handleSignupChange("lastName", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signupEmail">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="signupEmail"
                        type="email"
                        placeholder="your.email@example.com"
                        value={signupData.email}
                        onChange={(e) => handleSignupChange("email", e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={signupData.phone}
                        onChange={(e) => handleSignupChange("phone", e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signupPassword">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="signupPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        value={signupData.password}
                        onChange={(e) => handleSignupChange("password", e.target.value)}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm your password"
                        value={signupData.confirmPassword}
                        onChange={(e) => handleSignupChange("confirmPassword", e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agreeTerms"
                      checked={signupData.agreeTerms}
                      onCheckedChange={(checked) => handleSignupChange("agreeTerms", checked)}
                    />
                    <Label htmlFor="agreeTerms" className="text-sm leading-5">
                      I agree to the{" "}
                      <Link to="/terms" className="text-emerald-600 hover:text-emerald-700">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" className="text-emerald-600 hover:text-emerald-700">
                        Privacy Policy
                      </Link>
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white py-3"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Language selector · replaces demo credentials */}
            <div className="mt-6">
              <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '10px',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#64748b',
                textAlign: 'center',
                marginBottom: '8px',
              }}>Language · Idioma</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    try { localStorage.setItem('wunli_lang', 'en'); } catch (_) {}
                    window.location.reload();
                  }}
                  style={{
                    padding: '12px',
                    background: (typeof window !== 'undefined' && localStorage.getItem('wunli_lang') === 'en') ? '#0f172a' : '#ffffff',
                    color: (typeof window !== 'undefined' && localStorage.getItem('wunli_lang') === 'en') ? '#ffffff' : '#0f172a',
                    border: '1px solid #0f172a',
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '12px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >🇺🇸 English</button>
                <button
                  type="button"
                  onClick={() => {
                    try { localStorage.setItem('wunli_lang', 'es'); } catch (_) {}
                    window.location.reload();
                  }}
                  style={{
                    padding: '12px',
                    background: (typeof window !== 'undefined' && localStorage.getItem('wunli_lang') === 'es') ? '#0f172a' : '#ffffff',
                    color: (typeof window !== 'undefined' && localStorage.getItem('wunli_lang') === 'es') ? '#ffffff' : '#0f172a',
                    border: '1px solid #0f172a',
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '12px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >🇲🇽 Español</button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default CustomerAuth;